import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Row, Col, Typography, Input, Button, Space, Divider } from 'antd';
import { 
  FacebookFilled, 
  InstagramFilled, 
  TwitterSquareFilled, 
  LinkedinFilled, 
  ArrowRightOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined 
} from '@ant-design/icons';
import logoImage from '../assets/miran-logo.png'; 

const { Title, Text } = Typography;

// --- Styled Components ---

const FooterWrapper = styled.footer`
  background-color: #1a1a1a;
  color: #fff;
  padding: 5rem 5% 2rem;
  font-family: 'Lato', sans-serif;
  margin-top: auto; 

  /* Mobile Adjustment: Reduce padding on small screens */
  @media (max-width: 768px) {
    padding: 3rem 1.5rem 1rem;
    text-align: center;
  }
`;

const FooterLogo = styled.img`
  width: 150px;
  margin-bottom: 1.5rem;
  opacity: 0.9;
  /* Ensure logo centers on mobile */
  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
    display: block;
  }
`;

const SectionTitle = styled(Title)`
  && {
    color: #d4af37; /* Gold */
    font-family: 'Playfair Display', serif;
    margin-bottom: 1.5rem;
    font-size: 1.2rem;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    margin-bottom: 0.8rem;
  }

  a {
    color: #bfbfbf;
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 1rem;
    display: inline-flex;
    align-items: center;

    /* Center list items on mobile */
    @media (max-width: 768px) {
      justify-content: center;
    }

    &:hover {
      color: #d4af37;
      transform: translateX(5px);
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #bfbfbf;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  line-height: 1.6;
  
  /* Center align on mobile */
  @media (max-width: 768px) {
    justify-content: center;
    text-align: center;
  }

  .anticon {
    color: #d4af37;
    font-size: 1.1rem;
    margin-top: 3px;
  }

  a {
    color: #bfbfbf;
    text-decoration: none;
    transition: color 0.3s;
    &:hover { color: #d4af37; }
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 1.5rem;

  /* Center align on mobile */
  @media (max-width: 768px) {
    justify-content: center;
  }

  .anticon {
    font-size: 1.5rem;
    color: #fff;
    transition: color 0.3s ease;
    cursor: pointer;

    &:hover {
      color: #d4af37;
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  color: #666;
  margin-top: 2rem;
  font-size: 0.9rem;
`;

// Helper component to fix stacking issues
const MobileCenteredCol = styled(Col)`
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

// --- Component ---

const Footer = () => {
  return (
    <FooterWrapper>
      <Row gutter={[48, 48]}>
        
        {/* Column 1: Brand & Tagline */}
        <MobileCenteredCol xs={24} md={12} lg={6}>
          <FooterLogo src={logoImage} alt="Miran Suites" />
          <Text style={{ color: '#bfbfbf', lineHeight: '1.8', display: 'block', marginBottom: '1.5rem' }}>
            Experience the pinnacle of luxury and comfort. Miran Suites offers a sanctuary of elegance in the heart of the city.
          </Text>
          <SocialIcons>
            <FacebookFilled />
            <InstagramFilled />
            <TwitterSquareFilled />
            <LinkedinFilled />
          </SocialIcons>
        </MobileCenteredCol>

        {/* Column 2: Quick Links */}
        <MobileCenteredCol xs={24} md={12} lg={6}>
          <SectionTitle level={4}>Explore</SectionTitle>
          <LinkList>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/rooms">Suites & Rooms</Link></li>
            <li><Link to="/about">Our Story</Link></li>
            <li><Link to="/contact">Contact Us</Link></li>
            <li><a href="https://live.ipms247.com/booking/book-rooms-miranhotelssuites" target="_blank" rel="noreferrer">Book Now</a></li>
          </LinkList>
        </MobileCenteredCol>

        {/* Column 3: Contact Info */}
        <MobileCenteredCol xs={24} md={12} lg={6}>
          <SectionTitle level={4}>Contact</SectionTitle>
          
          <ContactItem>
            <EnvironmentOutlined />
            <a href="https://maps.app.goo.gl/aC3x7g441oTX6nxe9" target="_blank" rel="noreferrer">
              A 1502, Greenfields Colony,<br />Gate No 3, Faridabad,<br />Haryana 121010
            </a>
          </ContactItem>
          
          <ContactItem>
            <PhoneOutlined />
            <a href="tel:+919266134138">+91 9266134138</a>
          </ContactItem>
          
          <ContactItem>
            <MailOutlined />
            <a href="mailto:info@miransuites.com">info@miransuites.com</a>
          </ContactItem>
        </MobileCenteredCol>

        {/* Column 4: Newsletter */}
        <MobileCenteredCol xs={24} md={12} lg={6}>
          <SectionTitle level={4}>Newsletter</SectionTitle>
          <Text style={{ color: '#bfbfbf', marginBottom: '1rem', display: 'block' }}>
            Subscribe to receive exclusive offers and latest news.
          </Text>
          <Space.Compact style={{ width: '100%', maxWidth: '300px' }}>
            <Input placeholder="Your Email Address" style={{ borderRadius: '2px 0 0 2px' }} />
            <Button type="primary" style={{ background: '#d4af37', borderColor: '#d4af37', borderRadius: '0 2px 2px 0' }}>
              <ArrowRightOutlined />
            </Button>
          </Space.Compact>
        </MobileCenteredCol>
      </Row>

      <Divider style={{ borderColor: '#333', margin: '3rem 0 2rem' }} />

      <Copyright>
        <p>&copy; {new Date().getFullYear()} Miran Hotels & Suites. All rights reserved.</p>
        <Space split={<Divider type="vertical" style={{ borderColor: '#666' }} />}>
          <Link to="#" style={{ color: '#666' }}>Privacy Policy</Link>
          <Link to="#" style={{ color: '#666' }}>Terms of Service</Link>
        </Space>
      </Copyright>
    </FooterWrapper>
  );
};

export default Footer;